import { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { ColorfulLogo } from "@/components/ColorfulLogo";

interface StoryModalProps {
  open: boolean;
  onClose: () => void;
  onBookCall?: () => void;
}

const STATS = [
  { value: "10", label: "Years of building" },
  { value: "3", label: "Friends who started it" },
  { value: "1", label: "Core mission" },
  { value: "∞", label: "Value to add" },
];

const SECTIONS = [
  {
    tag: "2014 · The beginning",
    heading: "A group of friends with one stubborn belief.",
    body: "Ten years ago, a small group of friends who cared deeply about technology and even more about people started AgentsToGo. We weren't chasing venture capital or the next trend. We had a single, almost frustratingly simple mission: add the most value possible through technology. That was it. That was everything.",
    number: "01",
  },
  {
    tag: "2014 – 2024 · The work",
    heading: "A decade of proving the mission.",
    body: "We built software, automated workflows, designed systems, and partnered with companies across industries. Every engagement started and ended with the same question: did this add real value? Not vanity metrics. Not impressive demos. Real, measurable, lasting value for the people on the other side of the work.",
    number: "02",
  },
  {
    tag: "2024 · The discovery",
    heading: "The fastest way to add value just changed.",
    body: "Then AI agents arrived — and we saw it clearly before most: the highest-leverage way to add value isn't building another product or writing another line of custom code. It's building agents. Intelligent, autonomous systems that work around the clock, handle complexity without complaint, and scale infinitely without a salary. AgentsToGo was built for this moment.",
    number: "03",
  },
  {
    tag: "Today · The new chapter",
    heading: "AgentsToGo is our boldest move yet.",
    body: "AgentsToGo is a new chapter — the same mission, the same people, a radically better vehicle. We are thrilled to be building at the pace of this technology. Every agent we deliver is a piece of that original promise: technology that adds real value to real people's lives. We're just getting started, and we'd love to build yours.",
    number: "04",
  },
];

export function StoryModal({ open, onClose, onBookCall }: StoryModalProps) {
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (open) window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="story-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-50 scifi-wave-bg overflow-y-auto"
        >
          {/* Sticky nav */}
          <div className="sticky top-0 z-[200] bg-black/70 backdrop-blur-md border-b border-cyan-400/15 flex items-center justify-between px-6 sm:px-12 py-4">
            <ColorfulLogo className="text-base font-bold" />
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-cyan-300 transition-colors"
            >
              <X className="w-4 h-4" />
              <span className="hidden sm:inline">Close</span>
            </button>
          </div>

          {/* Hero */}
          <div className="relative text-white overflow-hidden border-b border-cyan-400/15">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-fuchsia-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />

            <div className="relative max-w-4xl mx-auto px-6 sm:px-12 py-24 sm:py-32">
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300 mb-6"
              >
                agentstogo.com · est. 2024
              </motion.p>
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18 }}
                className="text-4xl sm:text-6xl font-bold tracking-tight leading-[1.08] mb-8"
              >
                We exist to add<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-fuchsia-300">
                  the most value
                </span>
                <br />through technology.
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28 }}
                className="text-lg text-slate-300 max-w-xl leading-relaxed"
              >
                A decade ago, a group of friends started with this single mission.
                Today, we've found the fastest way to fulfil it yet — AI agents.
              </motion.p>
            </div>
          </div>

          {/* Stats strip */}
          <div className="bg-black/60 backdrop-blur-sm border-b border-cyan-400/15">
            <div className="max-w-4xl mx-auto px-6 sm:px-12 py-10 grid grid-cols-2 sm:grid-cols-4 gap-8">
              {STATS.map(({ value, label }) => (
                <div key={label} className="text-center">
                  <p className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-br from-cyan-300 to-fuchsia-400 leading-none mb-2">
                    {value}
                  </p>
                  <p className="text-xs text-slate-400 uppercase tracking-widest font-medium">{label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Story sections */}
          <div className="divide-y divide-cyan-400/10 bg-black/30">
            {SECTIONS.map((s, i) => (
              <motion.section
                key={s.number}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: 0.05 * i }}
              >
                <div className="max-w-4xl mx-auto px-6 sm:px-12 py-16 sm:py-20 grid sm:grid-cols-[1fr_2fr] gap-8 sm:gap-16 items-start">
                  {/* Left column */}
                  <div>
                    <span className="text-7xl sm:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br from-cyan-400/40 to-fuchsia-400/30 leading-none select-none block mb-4">
                      {s.number}
                    </span>
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-cyan-300">
                      {s.tag}
                    </p>
                  </div>
                  {/* Right column */}
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white leading-snug mb-5 tracking-tight">
                      {s.heading}
                    </h2>
                    <p className="text-base sm:text-lg text-slate-300 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              </motion.section>
            ))}
          </div>

          {/* Closing CTA */}
          <div className="relative text-white overflow-hidden border-t border-cyan-400/15">
            <div className="absolute inset-0 bg-gradient-to-br from-purple-950/60 via-fuchsia-950/40 to-cyan-950/60 pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative max-w-4xl mx-auto px-6 sm:px-12 py-20 sm:py-28 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300 mb-5">
                The mission continues
              </p>
              <h2 className="text-3xl sm:text-5xl font-bold leading-tight tracking-tight mb-6 text-white">
                We are thrilled to enjoy<br />the fast pace of technology.
              </h2>
              <p className="text-lg text-slate-300 max-w-lg mx-auto leading-relaxed mb-10">
                This is a new chapter for AgentsToGo — and we want to write it with you.
                Let's build something that matters.
              </p>
              <button
                onClick={() => { onClose(); onBookCall?.(); }}
                className="inline-flex items-center gap-2 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold px-8 py-3.5 rounded-full transition-colors text-sm"
                style={{ boxShadow: "0 0 24px rgba(0,229,255,0.45)" }}
              >
                Schedule Your Call
              </button>
            </div>
          </div>

          {/* Footer inside story */}
          <div className="bg-black/80 border-t border-cyan-400/15 text-slate-500 text-xs px-6 sm:px-12 py-6 text-center">
            agentstogo.com · est. 2024 · AgentsToGo · {new Date().getFullYear()}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
